import { Component, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { integrationActions } from '../../actions/integration.actions';
import {
    IAumicoCompanySummary,
    ICompanyMappingSuggestion,
} from '../../models/integration/company-mapping-suggestion.model';
import { ConnectionMappingVendor } from '../../models/integration/connection-mapping.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { ISingleEditState } from '../../reducer/integration.reducer';
import { IntegrationProvider } from '../../provider/integration.provider';
import { IntegrationService } from '../../services/integration.service';

/**
 * Single edit modal. Opens for exactly one BC company (via `startSingleEdit`).
 * On Save dispatches `upsertMapping` directly to persist via
 * `PUT /api/companies/:id/integrations/mappings`. On upsert success the modal
 * auto closes (via the upsertMappingSuccess action stream); the
 * `upsertMappingRefresh$` effect then refreshes the tenant detail table.
 *
 * Reads from the dedicated `singleEdit` state slice (disjoint from the
 * manage modal's `externalCompanies` slice), so opening / closing this modal
 * never invalidates the manage modal's underlying data.
 */
type ModeView = 'loading' | 'edit';

@Component({
    selector: 'aumico-company-mapping-modal',
    templateUrl: './company-mapping-modal.component.html',
    styleUrls: ['./company-mapping-modal.component.scss'],
})
export class CompanyMappingModalComponent implements OnInit, OnDestroy {
    public singleEditState$!: Observable<ISingleEditState>;
    public currentItem$!: Observable<ICompanyMappingSuggestion | null>;

    /** Local UI state: which view is shown within the modal. */
    public mode: ModeView = 'loading';

    /**
     * Catalog fetch state, passed down to the shared search input component
     * (`aumico-integrations-search-company-input-field`), which owns the search
     * term, filtering and dropdown rendering.
     */
    public searchIsLoading = true;
    public searchError = false;

    /**
     * Two stage commit: clicking a dropdown row stages the selection here
     * (visible as a card replacing the search input). The user then explicitly
     * clicks Save in the ham modal footer to persist. Cleaner than the prior
     * "click row to commit" flow: gives the user a chance to reconsider
     * before hitting the BE.
     */
    public stagedAumicoCompany: IAumicoCompanySummary | null = null;

    /**
     * Cached catalog stream: shared across this component instance's lifetime and
     * passed into the search input component as `options`.
     */
    public aumicoCatalog$!: Observable<IAumicoCompanySummary[]>;

    private readonly destroy$ = new Subject<void>();

    public constructor(
        private readonly integrationProvider: IntegrationProvider,
        private readonly integrationService: IntegrationService,
        private readonly actions$: Actions,
    ) {}

    public ngOnInit(): void {
        this.singleEditState$ = this.integrationProvider.getSingleEditState$();
        this.currentItem$ = this.singleEditState$.pipe(map((state) => state.suggestion));

        // Load the catalog once for this wizard session. shareReplay caches the
        // result so re entering search mode (or re typing) doesn't refetch.
        this.aumicoCatalog$ = this.integrationService.listAumicoCompanyOptions().pipe(
            map((companies) => {
                this.searchIsLoading = false;
                return companies;
            }),
            catchError(() => {
                this.searchIsLoading = false;
                this.searchError = true;
                return of([] as IAumicoCompanySummary[]);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        // Mode is driven only by whether a suggestion is loaded. The modal is
        // always in "edit" mode once a suggestion is in scope; the template
        // conditionally shows the "currently mapped" hint when there is one.
        this.singleEditState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            if (state.suggestion == null) {
                this.mode = 'loading';
                return;
            }
            this.mode = 'edit';
        });

        // Auto close on successful persist. The effect (`upsertMapping$`) fires
        // `upsertMappingSuccess` which we react to here by dispatching cancel
        // (which resets mapping state and hides the wizard). The same effect
        // also refreshes tenant detail so the manage modal table updates.
        this.actions$
            .pipe(ofType(integrationActions.upsertMappingSuccess), takeUntil(this.destroy$))
            .subscribe(() => {
                this.integrationProvider.cancelMapping();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── User actions ────────────────────────────────────────────────────────

    /**
     * ham modal Save click: persist the staged selection. Guarded by the
     * disabled state on ham-modal (`!stagedAumicoCompany`), so this should
     * never fire with nothing staged, but we defensively check anyway.
     *
     * Angular forbids pipes inside event handler expressions, so we can't pass
     * `currentItem$ | async` from the template. Instead we read the current
     * item from the latest mapping state synchronously via `take(1)`. Safe
     * because the wizard always has exactly one suggestion when this fires.
     */
    public onSave(): void {
        if (this.stagedAumicoCompany == null) return;
        const stagedId = this.stagedAumicoCompany.id;
        this.singleEditState$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((state) => {
                if (state.suggestion == null || state.connectionId == null) return;
                this.integrationProvider.upsertMapping(stagedId, {
                    // Vendor comes from the single-edit slice so this generic modal
                    // writes the mapping for whichever auto-match vendor opened it
                    // (D365 BC, Topal, ...). Falls back to D365 BC defensively. The
                    // slice holds the wider IntegrationVendor; narrow to the mapping
                    // vendor union (same idiom as the auto-match effect).
                    vendor: (state.vendor ?? IntegrationVendor.D365_BC) as ConnectionMappingVendor,
                    connectionId: state.connectionId,
                    externalCompanyId: state.suggestion.vendorCompany.externalCompanyId,
                    externalCompanyName: state.suggestion.vendorCompany.externalCompanyName,
                });
            });
    }

    /** Abbrechen / X / Esc: close the modal without changing anything. */
    public onCancel(): void {
        this.integrationProvider.cancelMapping();
    }
}
